import { iconForName } from "@/components/artifacts/iconForName.ts";
import { AvatarWithFallback } from "@/components/ui/avatar.tsx";
import { cn } from "@/lib/utils.ts";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";

type PersonHeaderComponentData = Extract<ArtifactComponent, { type: "personHeader" }>;

// Inline badge color (text only, no pill) — mirrors the legacy RSVP status
// label which is colored text + icon next to the responder name.
const badgeTextClass = (severity: NonNullable<PersonHeaderComponentData["badge"]>["severity"]) => {
  switch (severity) {
    case "success":
      return "text-green-600";
    case "warning":
      return "text-amber-600";
    case "error":
      return "text-red-600";
    case "muted":
      return "text-muted-foreground";
    case "info":
    default:
      return "text-blue-600";
  }
};

export const PersonHeaderBlock = ({ component }: { component: PersonHeaderComponentData }) => {
  const p = component.participant;
  const name = p.name || p.value || "Unknown";
  const BadgeIcon = component.badge?.icon ? iconForName(component.badge.icon) : null;

  return (
    <div className="flex items-center gap-2">
      {/* With a subtitle the header is two lines, so use a larger avatar that
          spans both; single-line headers keep the compact size. */}
      <AvatarWithFallback
        size={component.subtitle ? "lg" : "sm"}
        person={{ ...p, name, value: p.value || name }}
      />
      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          <span className="font-semibold">{name}</span>
          {component.badge ? (
            <span
              className={cn(
                "flex items-center gap-1 text-sm font-medium",
                badgeTextClass(component.badge.severity),
              )}
            >
              {BadgeIcon ? <BadgeIcon className="size-4" /> : null}
              {component.badge.label}
            </span>
          ) : null}
        </div>
        {component.subtitle ? (
          <span className="text-sm text-muted-foreground">{component.subtitle}</span>
        ) : null}
      </div>
    </div>
  );
};
